import React, { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api/nocode';
import { TABLES } from '../../api/tables';
import EnchargeForm from '../../components/EnchargeForm';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';

const ACADEMY_URL = 'https://academy.gospelwiseauthor.com';
const LOGIN_URL = `https://gospelwiseauthor.app/#/login?redirect=${ACADEMY_URL}`;
const LOGO_URL = 'https://www.crossandpen.com/wp-content/uploads/2026/08/gwa-academy_logo.png';
const HEADSHOT_URL = 'https://www.faibowrimo.com/wp-content/uploads/2026/05/jb-1_rec.jpg';
const ENCHARGE_FORM_ID = 'd8d726cb-4b43-4393-839c-f1f8adcce798';
const VIDEO_URL = 'https://play.gumlet.io/embed/REPLACE_WITH_ACADEMY_WELCOME_VIDEO_ID?background=false&autoplay=false&loop=false&disable_player_controls=false';

const scrollToWaitlist = event => {
  event.preventDefault();
  document.getElementById('list')?.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  });
};

const FadeInSection = ({ children, className = '' }) => {
  const [isVisible, setVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const node = domRef.current;
    const observer = new IntersectionObserver(
      entries => entries.forEach(entry => {
        if (entry.isIntersecting) setVisible(true);
      }),
      { threshold: 0.1 }
    );

    if (node) observer.observe(node);
    return () => {
      if (node) observer.unobserve(node);
    };
  }, []);

  return (
    <div
      ref={domRef}
      className={`transition-all duration-1000 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
      } ${className}`}
    >
      {children}
    </div>
  );
};

const RibbonLabel = ({ children, className = '' }) => (
  <div className={`ribbon-label ${className}`}>{children}</div>
);

const FAQItem = ({ q, a, isOpen, onClick }) => (
  <div className="border border-hairline rounded-lg mb-4 bg-white overflow-hidden shadow-card">
    <button
      type="button"
      className="w-full text-left px-6 py-4 font-heading font-bold text-navy flex justify-between items-center hover:bg-parchment/60 transition-colors"
      onClick={onClick}
    >
      <span className="pr-4">{q}</span>
      <SafeIcon name={isOpen ? 'ChevronUp' : 'ChevronDown'} className="text-orange shrink-0" />
    </button>
    <div className={`px-6 text-slate overflow-hidden transition-all duration-300 ${
      isOpen ? 'max-h-96 pb-5 opacity-100' : 'max-h-0 opacity-0'
    }`}>
      {a}
    </div>
  </div>
);

const RHYTHM = [
  {
    when: 'Morning',
    title: 'Morning Lesson',
    card: 'bg-tint-blue border-tint-border-blue',
    edge: 'edge-bar blue',
    whenColor: 'text-blue',
    body: 'A short teaching video drops in the Academy. Watch it with coffee, on the treadmill, in the school pickup line. Fiction and nonfiction examples every single day.',
  },
  {
    when: '1:00pm ET · Live',
    title: 'Midday Lab',
    card: 'bg-tint-orange border-tint-border-orange',
    edge: 'edge-bar orange',
    whenColor: 'text-[#C4562F]',
    body: 'We get on together and actually build it. Short setup, then timed work blocks with me coaching live while you fill in your framework.',
  },
  {
    when: 'Afternoon',
    title: 'Study Hall',
    card: 'bg-tint-green border-tint-border-green',
    edge: 'edge-bar green',
    whenColor: 'text-[#3B7A41]',
    body: "Your own quiet block to finish the day's movement in the StoryWise Framework Template. It saves as you go, so nothing gets lost.",
  },
  {
    when: 'Evening',
    title: 'The Commons',
    card: 'bg-tint-red border-tint-border-red',
    edge: 'edge-bar red',
    whenColor: 'text-[#C0341D]',
    body: 'The class gathers in the community to share what took shape that day. This is where the encouragement lives and where the stuck get unstuck.',
  },
];

const DAYS = [
  {
    dow: 'MON',
    n: 1,
    tag: 'Foundations',
    title: 'The raw material',
    body: 'Characters, themes, the idea underneath the idea. We name who this book is for and what it is actually about before a single movement gets mapped.',
  },
  {
    dow: 'TUE',
    n: 2,
    tag: 'Movement One',
    title: 'Setup',
    body: 'Opening scene, inciting incident, first plot point. The first quarter of your book stops being a guess.',
  },
  {
    dow: 'WED',
    n: 3,
    tag: 'Movement Two',
    title: 'Reaction',
    body: 'The pinch point and the midpoint shift. This is the day the soggy middle finally gets a spine.',
  },
  {
    dow: 'THU',
    n: 4,
    tag: 'Movement Three',
    title: 'Attack',
    body: 'Second pinch point, second plot point. Everything starts driving toward the ending on purpose.',
  },
  {
    dow: 'FRI',
    n: 5,
    tag: 'Movement Four & Commencement',
    title: 'Resolution',
    last: true,
    body: 'Climax, resolution, and a full read-through of the thing you just built. Then we export it, celebrate it, and send you out with it.',
  },
];

const WALK_AWAY = [
  ['A completed StoryWise Framework Template.', 'All nine movements, filled in, exported, yours to keep and write from.'],
  ['A middle that holds.', 'Most books die in the second act. You will leave knowing exactly what happens there and why.'],
  ['Language for what your book is about.', 'The kind of clarity that makes describing it to someone else feel easy instead of embarrassing.'],
  ['A Lab Partner and a class.', 'Writers in your genre who watched you build it and will still be there in October.'],
  ['Momentum.', 'The blank page is not blank anymore. That changes what November looks like.'],
];

const BASE_INCLUDES = [
  'All five Morning Lessons',
  'Daily Midday Lab, live at 1pm ET',
  'Study Hall with the StoryWise Framework Template',
  'The Commons community, all week',
  'Your completed Template, exported Friday',
  'Fiction and nonfiction tracks',
  'Ongoing lesson + template access',
];

const AAP_INCLUDES = [
  'Lab Partner matching, by genre and stage',
  'Permanent replay of every Midday Lab',
  'Private podcast feed for the week',
  'Printable StoryWise Framework Kit',
  'Completion certificate',
  'StoryWise Formula Training ($37 value)',
  '$40 Credit toward StoryWise Framework Workshop',
];

const FOR_YOU = [
  'You are a Christian writer with a book idea and no working structure underneath it.',
  'You write fiction or nonfiction. Both tracks run side by side, every day, with their own examples.',
  'You have started this book before and stalled somewhere in the middle.',
  'You have bought the training and never quite carved out the week to use it.',
  'You do better with a guide beside you and a class around you than with a course tab left open.',
];

const FAQS = [
  {
    q: 'What exactly do I walk away with?',
    a: 'A completed StoryWise Framework Template — all nine movements filled in, exported, and yours to keep. Not notes about planning. An actual plan you can write from.',
  },
  {
    q: 'I write nonfiction. Is this for novelists only?',
    a: 'Both tracks run side by side, every single day, with their own examples and their own prompts. The StoryWise Framework applies to memoir, Bible study, devotional, and teaching books just as much as it does to fiction.',
  },
  {
    q: 'How much time does this take each day?',
    a: 'About an hour. The Morning Lesson is short, the Midday Lab runs live at 1pm ET, and Study Hall is your own quiet block to finish the day’s movement. The Commons is asynchronous and takes as long as you want it to.',
  },
  {
    q: 'What if I cannot make the 1pm Lab live?',
    a: 'You can still complete the week. The Morning Lesson carries the teaching, and Study Hall is where the building happens. All-Access includes replay so you can catch the Lab on your own schedule.',
  },
  {
    q: 'What is the difference between the two tiers?',
    a: 'The base session is the complete week — every lesson, every Lab, Study Hall, The Commons, and the StoryWise Framework Template. All-Access adds the things that make it personal and lasting: Lab Partner matching, permanent replay, the private podcast feed, the printable Framework Kit, your certificate, and credit toward the StoryWise Framework.',
  },
  {
    q: 'I have never done anything with GospelWise Author before. Can I still come?',
    a: 'Yes. The Creator Session stands entirely on its own. No prior workshop, course, or camp required.',
  },
];

export default function HomePage() {
  const { user } = useAuth();
  const [settings, setSettings] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(TABLES.settings);
        const rows = Array.isArray(res) ? res : (res?.data ?? []);
        const map = {};
        if (Array.isArray(rows)) {
          rows.forEach(setting => {
            if (setting?.setting_key) {
              map[setting.setting_key] = setting.value;
            }
          });
        }
        setSettings(map);
      } catch (error) {
        console.error('Failed to load settings', error);
      } finally {
        setLoadingConfig(false);
      }
    })();
  }, []);

  if (user) {
    return <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} replace />;
  }

  const isRegistrationOpen = String(settings.registration_open ?? '0') === '1';
  const showWaitlist = !loadingConfig && !isRegistrationOpen;
  const showRegister = !loadingConfig && isRegistrationOpen;

  return (
    <div className="min-h-screen bg-parchment text-ink flex flex-col overflow-x-hidden">
      <nav className={`fixed top-0 w-full z-50 bg-white border-b border-hairline transition-shadow ${
        scrolled ? 'shadow-md' : ''
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 bg-blue rounded-lg flex items-center justify-center text-white shrink-0">
                <SafeIcon name="BookOpen" className="text-xl" />
              </div>
              <span className="font-heading font-bold text-base sm:text-lg text-navy tracking-tight">
                GospelWise Author Academy
              </span>
            </div>

            <div className="hidden md:flex items-center gap-3">
              {showRegister && (
                <a href="#/register">
                  <Button className="btn-press bg-orange text-white font-heading font-bold px-6 py-2.5 rounded-lg border-none">
                    Enroll Now
                  </Button>
                </a>
              )}
              {showWaitlist && (
                <a href="#list" onClick={scrollToWaitlist}>
                  <Button className="btn-press bg-orange text-white font-heading font-bold px-6 py-2.5 rounded-lg border-none">
                    Join the List
                  </Button>
                </a>
              )}
              <a href={LOGIN_URL}>
                <Button variant="outline" className="border-2 border-blue text-blue hover:bg-blue hover:text-white font-heading font-semibold px-6 py-2.5 rounded-lg">
                  Log In
                </Button>
              </a>
            </div>

            <button
              type="button"
              className="md:hidden text-navy p-2"
              onClick={() => setIsMobileMenuOpen(value => !value)}
            >
              <SafeIcon name={isMobileMenuOpen ? 'X' : 'Menu'} className="text-3xl" />
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-b border-hairline px-4 pt-2 pb-6 space-y-3 shadow-lg absolute w-full">
            <a
              href={showRegister ? '#/register' : '#list'}
              onClick={showWaitlist ? scrollToWaitlist : undefined}
              className="block"
            >
              <Button className="btn-press w-full bg-orange text-white py-3 text-lg border-none">
                {showRegister ? 'Enroll Now' : 'Join the List'}
              </Button>
            </a>
            <a href={LOGIN_URL} className="block">
              <Button variant="outline" className="w-full border-2 border-blue text-blue py-3 text-lg">
                Log In
              </Button>
            </a>
          </div>
        )}
      </nav>

      <header className="paper relative pt-32 md:pt-36 pb-20 md:pb-28 border-b-8 border-orange">
        <div className="absolute top-20 left-0 right-0 h-2 bg-blue" />
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20 items-center">
            <FadeInSection className="order-1 md:order-2 flex flex-col items-center w-full">
              <img
                src="https://www.crossandpen.com/wp-content/uploads/2026/08/gwa-academy_logo-banner.png"
                alt="The Creator Session — GospelWise Author Academy"
                className="w-full max-w-md lg:max-w-lg h-auto"
                width="1536"
                height="1024"
              />
              <p className="hand text-2xl md:text-3xl text-center mt-1 mb-6">
                Your Story. God&rsquo;s Glory.
              </p>
              <div className="w-full max-w-lg aspect-video rounded-xl overflow-hidden shadow-card-lg border-4 border-navy bg-navy">
                <iframe
                  src={VIDEO_URL}
                  title="Welcome to The Creator Session"
                  className="w-full h-full border-none"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </FadeInSection>

            <FadeInSection className="order-2 md:order-1 text-center md:text-left">
              <div className="inline-block bg-ribbon text-white font-heading font-bold text-xs tracking-[0.16em] uppercase px-5 py-2 rounded-sm shadow-card mb-7">
                September 14&ndash;18, 2026 &nbsp;&middot;&nbsp; Enrollment opens soon
              </div>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold text-navy leading-[1.08] mb-6">
                Walk in with an idea.<br className="hidden md:block" /> Walk out with{' '}
                <span className="text-blue">the plan.</span>
              </h1>
              <p className="text-lg md:text-xl text-slate leading-relaxed mb-7 max-w-xl mx-auto md:mx-0">
                Five days. One framework. The first session of the{' '}
                <strong className="text-ink font-bold">GospelWise Author Academy</strong> takes the
                book living in your head and gets it onto paper as a structure you can actually write from.
              </p>
              <div className="flex flex-wrap justify-center md:justify-start gap-x-7 gap-y-2.5 font-heading font-semibold text-sm tracking-wide text-blue mb-9">
                {['5 days', 'Live at 1pm ET', 'Fiction & nonfiction', 'About an hour a day'].map(text => (
                  <span key={text} className="inline-flex items-center gap-2">
                    <i className="w-[7px] h-[7px] rounded-full bg-orange inline-block" />
                    {text}
                  </span>
                ))}
              </div>
              <div className="min-h-[120px]">
                {loadingConfig ? (
                  <div className="flex items-center justify-center md:justify-start text-slate">
                    <SafeIcon name="Loader" className="animate-spin text-2xl mr-3" /> Loading&hellip;
                  </div>
                ) : showWaitlist ? (
                  <div className="space-y-3">
                    <a href="#list" onClick={scrollToWaitlist} className="inline-block w-full sm:w-auto">
                      <Button className="btn-press w-full sm:w-auto bg-orange text-white font-heading font-bold text-lg px-10 py-4 rounded-lg border-none">
                        Put Me on the List
                      </Button>
                    </a>
                    <p className="text-sm text-slate">The list hears first when doors open.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                      <a href="#/register">
                        <Button className="btn-press w-full sm:w-auto bg-orange text-white font-heading font-bold text-lg px-9 py-4 rounded-lg border-none">
                          Enroll for $27
                        </Button>
                      </a>
                      <a href={LOGIN_URL}>
                        <Button variant="outline" className="w-full sm:w-auto border-2 border-blue text-blue hover:bg-blue hover:text-white font-heading font-bold text-lg px-8 py-4 rounded-lg">
                          I Already Enrolled
                        </Button>
                      </a>
                    </div>
                    <p className="text-sm text-slate">All-Access $47 &middot; Fiction and nonfiction tracks</p>
                  </div>
                )}
              </div>
            </FadeInSection>
          </div>
        </div>
      </header>

      <section className="py-20 md:py-24">
        <div className="max-w-3xl mx-auto px-5">
          <FadeInSection className="text-center mb-9">
            <RibbonLabel>Class is in session</RibbonLabel>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy mt-4">
              You have never been short on ideas.
            </h2>
          </FadeInSection>
          <FadeInSection className="space-y-4 text-lg">
            <p>You have characters. You have a message. You have three-quarters of a chapter, a folder of notes, and a document you have opened forty times this year.</p>
            <p>What you do not have is the shape of it. Where the story turns. What belongs in chapter four. Why the middle keeps going soft.</p>
            <p><strong className="font-bold">The problem has never been your calling.</strong> It is that nobody ever sat down beside you and walked you through the structure, one movement at a time, until the whole thing held together.</p>
            <p>That is the entire point of this week.</p>
            <p className="hand text-2xl text-center pt-4">Five days. One finished plan. Together.</p>
          </FadeInSection>
        </div>
      </section>

      <section className="paper py-20 md:py-24 border-y border-hairline">
        <div className="max-w-5xl mx-auto px-5">
          <FadeInSection className="text-center mb-11">
            <RibbonLabel>The daily rhythm</RibbonLabel>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy mt-4 mb-3">
              Every day runs the same four beats
            </h2>
            <p className="text-slate max-w-xl mx-auto">
              Learn it in the morning. Build it in the afternoon. Share it at night.
              About an hour a day, and you keep the work.
            </p>
          </FadeInSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {RHYTHM.map(item => (
              <FadeInSection key={item.title} className={`${item.edge} ${item.card} border rounded-card p-7 pl-8 shadow-card`}>
                <div className={`font-heading text-xs font-semibold tracking-[0.12em] uppercase mb-2 ${item.whenColor}`}>
                  {item.when}
                </div>
                <h3 className="font-heading text-xl font-bold text-navy mb-2">{item.title}</h3>
                <p className="text-[#3E4A54]">{item.body}</p>
              </FadeInSection>
            ))}
          </div>
          <p className="hand text-2xl text-center mt-10">
            Plus a Lab Partner, matched to your genre, so you are not doing this alone.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-24">
        <div className="max-w-4xl mx-auto px-5">
          <FadeInSection className="text-center mb-11">
            <RibbonLabel>The week</RibbonLabel>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy mt-4 mb-3">
              Five days, four movements, one finished framework
            </h2>
            <p className="text-slate max-w-xl mx-auto">
              The StoryWise Framework, taken one movement at a time, so nothing piles up and nothing gets skipped.
            </p>
          </FadeInSection>
          <FadeInSection className="paper rounded-2xl border border-hairline py-2 shadow-card">
            {DAYS.map(day => (
              <div key={day.n} className={`flex gap-5 items-start px-5 sm:px-7 py-6 ${!day.last ? 'border-b border-rule' : ''}`}>
                <div className={`shrink-0 w-16 text-center text-white rounded-lg py-2 ${day.last ? 'bg-ribbon' : 'bg-blue'}`}>
                  <span className="block font-heading text-[11px] font-semibold tracking-[0.12em] opacity-85">{day.dow}</span>
                  <span className="block font-heading text-2xl font-extrabold leading-none mt-0.5">{day.n}</span>
                </div>
                <div>
                  <span className="font-heading text-[11px] font-semibold tracking-[0.1em] uppercase text-orange">{day.tag}</span>
                  <h3 className="font-heading text-lg font-bold text-navy mt-0.5 mb-1">{day.title}</h3>
                  <p className="text-slate">{day.body}</p>
                </div>
              </div>
            ))}
          </FadeInSection>
        </div>
      </section>

      <section className="bg-navy py-20 md:py-24">
        <div className="max-w-3xl mx-auto px-5">
          <FadeInSection className="text-center mb-11">
            <div className="ribbon-label bg-orange">Friday afternoon</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mt-4 mb-3">
              What you carry out of here
            </h2>
            <p className="text-white/75">Not notes about planning. An actual plan.</p>
          </FadeInSection>
          <div className="grid gap-3.5">
            {WALK_AWAY.map(([lead, rest]) => (
              <FadeInSection key={lead} className="flex gap-4 items-start bg-white/[0.07] rounded-xl px-5 py-4">
                <div className="shrink-0 w-7 h-7 rounded-full bg-pencil text-navy font-heading font-extrabold text-sm flex items-center justify-center">
                  &#10003;
                </div>
                <p className="text-white/90"><strong className="text-white font-bold">{lead}</strong> {rest}</p>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24">
        <div className="max-w-4xl mx-auto px-5">
          <FadeInSection className="text-center mb-11">
            <RibbonLabel>Two ways in</RibbonLabel>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy mt-4">
              The week is live and yours. All-Access accelerates your prep.
            </h2>
          </FadeInSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
            <FadeInSection className="bg-white rounded-2xl p-8 border border-hairline shadow-card flex flex-col">
              <h3 className="font-heading text-xl font-bold text-navy text-center">The Creator Session</h3>
              <div className="text-center my-5">
                <span className="font-heading text-5xl font-extrabold text-navy">$27</span>
              </div>
              <ul className="space-y-3.5 flex-1 mb-7">
                {BASE_INCLUDES.map(text => (
                  <li key={text} className="flex items-start gap-3">
                    <SafeIcon name="Check" className="text-blue text-lg shrink-0 mt-1" />
                    <span className="text-slate">{text}</span>
                  </li>
                ))}
              </ul>
              {showRegister && (
                <a href="#/register" className="mt-auto">
                  <Button variant="outline" className="w-full border-2 border-blue text-blue hover:bg-blue hover:text-white font-heading font-bold py-3.5 rounded-lg">
                    Enroll for $27
                  </Button>
                </a>
              )}
            </FadeInSection>

            <FadeInSection className="bg-navy rounded-2xl p-8 shadow-card-lg relative flex flex-col md:-translate-y-3">
              <div className="absolute top-0 right-0 bg-pencil text-navy font-heading text-[11px] font-bold px-4 py-1.5 uppercase tracking-[0.14em] rounded-bl-lg rounded-tr-2xl">
                Most Popular
              </div>
              <h3 className="font-heading text-xl font-bold text-white text-center mt-2">All-Access Pass</h3>
              <div className="text-center my-5">
                <span className="font-heading text-5xl font-extrabold text-white">$47</span>
              </div>
              <ul className="space-y-3.5 flex-1 mb-7">
                <li className="flex items-start gap-3 border-b border-white/20 pb-4">
                  <SafeIcon name="Plus" className="text-pencil text-lg shrink-0 mt-1" />
                  <span className="text-white font-bold italic">Everything above, plus:</span>
                </li>
                {AAP_INCLUDES.map(text => (
                  <li key={text} className="flex items-start gap-3">
                    <SafeIcon name="Star" className="text-pencil text-lg shrink-0 mt-1" />
                    <span className="text-white/90">{text}</span>
                  </li>
                ))}
              </ul>
              {showRegister && (
                <a href="#/register?plan=allaccess" className="mt-auto">
                  <Button className="btn-press w-full bg-orange text-white font-heading font-bold text-lg py-4 rounded-lg border-none">
                    Get All-Access &mdash; $47
                  </Button>
                </a>
              )}
            </FadeInSection>
          </div>
        </div>
      </section>

      <section className="paper py-20 md:py-24 border-y border-hairline">
        <div className="max-w-3xl mx-auto px-5">
          <FadeInSection className="text-center mb-9">
            <RibbonLabel>Who belongs in this room</RibbonLabel>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy mt-4">
              This week is built for you if&hellip;
            </h2>
          </FadeInSection>
          <FadeInSection>
            <ul className="grid gap-3">
              {FOR_YOU.map(text => (
                <li key={text} className="relative pl-8 text-lg text-slate">
                  <span className="absolute left-2 top-[11px] w-2 h-2 rounded-full bg-orange" />
                  {text}
                </li>
              ))}
            </ul>
            <p className="hand text-2xl text-center mt-8">If that is you, get on the list.</p>
          </FadeInSection>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-5">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
            <FadeInSection className="md:col-span-4 flex justify-center">
              <div className="w-44 h-44 md:w-full md:aspect-square rounded-full overflow-hidden border-4 border-pencil shadow-card-lg">
                <img src={HEADSHOT_URL} alt="Jeremy Bouma" className="w-full h-full object-cover" />
              </div>
            </FadeInSection>
            <FadeInSection className="md:col-span-8 text-center md:text-left space-y-4">
              <div className="font-heading text-xs font-bold tracking-[0.16em] uppercase text-orange">
                Your instructor
              </div>
              <h2 className="font-heading text-3xl font-bold text-navy">Jeremy Bouma</h2>
              <p className="text-lg text-slate">
                I have published over 70 books &mdash; fiction, nonfiction, a study Bible, audiobooks &mdash;
                as an indie author through EmmausWay Media Group. I know what it takes to move a manuscript
                from calling to published. I also know what it feels like when the book sits unfinished
                because life got in the way. This week exists because your story deserves to be told.
              </p>
              <p className="hand text-2xl pt-1">Wield the pen for the sake of the cross.</p>
            </FadeInSection>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24">
        <div className="max-w-3xl mx-auto px-5">
          <FadeInSection className="text-center mb-11">
            <RibbonLabel>Before you enroll</RibbonLabel>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy mt-4">
              Questions about the week
            </h2>
          </FadeInSection>
          <FadeInSection>
            {FAQS.map((faq, index) => (
              <FAQItem
                key={faq.q}
                q={faq.q}
                a={faq.a}
                isOpen={openFaqIndex === index}
                onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
              />
            ))}
          </FadeInSection>
        </div>
      </section>

      <section className="bg-navy border-t-8 border-orange py-20 md:py-24" id="list">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <FadeInSection className="space-y-6">
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-white leading-tight">
              Your book is waiting. Five days is all it takes to give it a shape.
            </h2>
            <p className="text-lg text-white/80">
              The Creator Session &middot; September 14&ndash;18, 2026
            </p>
            <div className="pt-4 flex justify-center">
              {loadingConfig ? (
                <div className="text-white/80 flex items-center min-h-[120px]">
                  <SafeIcon name="Loader" className="animate-spin mr-2" /> Loading&hellip;
                </div>
              ) : showWaitlist ? (
                <div className="bg-white rounded-2xl p-8 md:p-10 max-w-lg w-full mx-auto shadow-card-lg text-left">
                  <h3 className="font-heading text-2xl font-bold text-navy text-center mb-2">
                    Get on the list
                  </h3>
                  <p className="text-slate text-center text-sm mb-6">
                    Enrollment opens in early September, and the list hears before anyone else.
                  </p>
                  <EnchargeForm formId={ENCHARGE_FORM_ID} />
                  <p className="text-xs text-slate text-center mt-4">
                    One email when doors open, plus the week&rsquo;s details. Unsubscribe anytime.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <a href="#/register">
                    <Button className="btn-press bg-orange text-white font-heading font-bold text-xl px-12 py-5 rounded-xl border-none">
                      Enroll Now
                    </Button>
                  </a>
                  <p className="text-sm text-white/70">$27 for the week &middot; All-Access $47</p>
                </div>
              )}
            </div>
          </FadeInSection>
        </div>
      </section>

      <footer className="bg-navy border-t border-white/10 py-10 text-center">
        <div className="max-w-5xl mx-auto px-5">
          <div className="font-heading font-bold tracking-[0.16em] text-white text-base mb-1.5">
            CROSS <span className="text-orange">+</span> PEN
          </div>
          <div className="italic text-white/75 text-sm mb-4">
            Wield the pen for the sake of the cross.
          </div>
          <div className="text-white/60 text-sm space-x-2">
            <span>&copy; 2026 CROSS + PEN</span>
            <span>|</span>
            <a href="https://crossandpen.com" className="text-orange hover:underline">crossandpen.com</a>
            <span>|</span>
            <a href="https://gospelwiseauthor.com" className="text-orange hover:underline">gospelwiseauthor.com</a>
            <span>|</span>
            <a href="https://crossandpen.com/privacy" className="text-orange hover:underline">Privacy</a>
            <span>|</span>
            <a href="https://crossandpen.com/contact" className="text-orange hover:underline">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}